import { Toaster } from "@npco/zeller-design-system";
import { safe, unwrap } from "pb.safe";
import type { Identity } from "pb.types";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
	POSConnectorClient,
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
	type Payload,
	type POSConnectorMessage,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import type { Unsafe } from "../../../shared/unsafe";
import * as transport from "../../../transport";
import { DeclineResponse_fromRefundPosConnectorResponse } from "../shared/declined-response";
import { getPageProps } from "../shared/get-declined-page-props";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import type { RefundUnmatchedRequest } from "../terminal-refund";
import { getApprovedResponse } from "./refund-unmatched/get-approved-response";
import { Page } from "./refund-unmatched/page";
import { render } from "./refund-unmatched/render";
import {
	RefundAcknowledgedSchema,
	RefundResponseSchema,
	RefundSignatureVerificationSchema,
	RefundTerminalBusySchema,
	RefundTransactionEventSchema,
	type RefundResponse,
} from "./shared/schemas";

const resolve = transport.Resolve<"Terminal_refund">();

export namespace RefundUnmatched {
	export type Props = {
		request: RefundUnmatchedRequest;
	};
}

export function RefundUnmatched(props: RefundUnmatched.Props) {
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();
	const toaster = Toaster.useToaster();

	const { request } = props;

	const [sessionId] = useState(() => request.query.sessionUuid ?? uuidv4());

	const [page, _setPage] = useState<Page>(() => Page.Initialising());
	const setPage = useCallback(
		(nextOrFn: Page | ((prev: Page) => Page)) => {
			// not supported in refunds  at present but keep for future support
			if (!request.interactive) {
				return undefined;
			}
			return void _setPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				return next;
			});
		},
		[request.interactive],
	);

	const CancelRequest = useCallback(
		() =>
			({
				action: "transactionEvent",
				eventId: uuidv4(),
				timestamp: Date.now().toString(),
				data: {
					sessionUuid: sessionId,
					event: "CANCEL",
				},
			}) satisfies Payload,
		[sessionId],
	);

	const RefundUnmatchedRequest = useCallback(() => {
		const now = new Date();
		return {
			action: "refund",
			eventId: uuidv4(),
			timestamp: now.getTime().toString(),
			data: {
				// Currently required by backend.
				transactionUuid: uuidv4(),
				sessionUuid: sessionId,
				amount: request.query.amount,
				externalReference: request.query.reference ?? "",
				isCardPresent: true,
				terminalContact: request.query.terminalContact,
				terminalNote: request.query.terminalNote,
				terminalReceipt: request.query.terminalReceipt,
				terminalReceiptPrint: request.query.terminalReceiptPrint,
			},
		} satisfies Payload;
	}, [request, sessionId]);

	useEffect(() => {
		transport.onCancel(() => {
			return void done(
				Page.Cancelled({ onClose: closeWithCancelled }),
				"onClose",
				{ skip: true },
			);
		});

		let _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let _connection: POSConnectorClient | undefined = undefined;

		function dispose() {
			toaster.clear();
			transport.onCancel(undefined);
			if (_timeout) _timeout = void clearTimeout(_timeout);
			if (_connection) _connection = void _connection.close();
		}

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		function done<TPage extends Page>(
			page: TPage,
			onNonInteractive: keyof TPage,
			options?: { skip?: boolean },
		): void {
			// Any "done" call should close and cleanup everything all the time.
			dispose();

			if (done.isLocked) {
				return;
			} else {
				done.isLocked = true;
			}

			// Default to `skip: true` if `skip` undefined and request is not interactive.
			const skip = options?.skip ?? !request.interactive;

			return void (skip
				? (page[onNonInteractive] as Unsafe<() => void>)()
				: setPage(page));
		}
		done.isLocked = false;

		const _deviceId = request.headers.deviceId;
		if (!_deviceId) {
			return void (() =>
				done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not configured.",
							}),
					}),
					"onClose",
				))();
		}

		const deviceId = _deviceId;

		main();
		async function main() {
			const onRetry = () => void main();

			done.isLocked = false;
			setPage(Page.Initialising());

			// Prevent continuation if token resolve _after_ timeout.
			if (done.isLocked) {
				return;
			}

			/**
			 * `0` is the initial attampt and used when resetting between reconnection attempts.
			 * `5` is last/final attempt.
			 */
			let _attempt = 0;
			let _hasRequested = false;

			connect();
			async function connect() {
				logger.debug("refund: unmatched connect", { attempt: _attempt });

				updateTimeout(() => {
					return void done(
						Page.ErrorOperation({
							onClose: closeWithNetworkFailure,
							onRetry,
						}),
						"onClose",
					);
				}, STATE_INITIAL_TIMEOUT);

				const authToken = (await request.tokens())?.authAccessToken;

				// Prevent continuation if token resolve _after_ timeout.
				if (done.isLocked) {
					return;
				}

				if (!authToken) {
					return void (() =>
						done(
							Page.ErrorOperation({
								onClose: () =>
									void resolve.Error("Setup Required", {
										message: "Not authenticated.",
									}),
							}),
							"onClose",
						))();
				}

				transport.onCancel(() => {
					if (!connection.ready) {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled }),
							"onClose",
							{ skip: true },
						);
					}

					return void cancel();
				});

				const connection = POSConnectorClient({
					authToken,
					deviceUuid: deviceId,
					onOpen,
					onClose,
					onError,
				});
				_connection = connection;

				/**
				 * Timeout for showing reconnected notification to prevent flickering
				 * if continually failing to open stable connection.
				 */
				let _timeoutReconnected: ReturnType<typeof setTimeout> | undefined =
					undefined;

				function onOpen() {
					logger.debug("refund: unmatched open");

					if (!_hasRequested) {
						begin();
					} else {
						listen();

						/* v8 ignore next 11 -- SUPPRESS_STATUS_NOTIFICATIONS is a
						   compile-time `true` flag, so this status-notification block is
						   intentionally dead until the flag is flipped. */
						if (!SUPPRESS_STATUS_NOTIFICATIONS) {
							_timeoutReconnected =
								(void clearTimeout(_timeoutReconnected),
								setTimeout(() => {
									toaster.toast({
										message: "Reconnected!",
										status: "positive",
										timeout: "fast",
									});
								}, 1000));
						}
					}
				}

				function reconnect() {
					/* v8 ignore next 4 -- SUPPRESS_STATUS_NOTIFICATIONS is a compile-time
					   `true` flag, so this status-notification block is intentionally dead
					   until the flag is flipped. */
					if (!SUPPRESS_STATUS_NOTIFICATIONS) {
						clearTimeout(_timeoutReconnected);
						toaster.await({ message: "Reconnecting…" });
					}

					logger.warn("refund: unmatched pos connector client reconnecting", {
						attempt: _attempt,
					});

					if (_attempt >= 5) {
						/* v8 ignore next 6 -- SUPPRESS_STATUS_NOTIFICATIONS is a
						   compile-time `true` flag, so this status-notification block is
						   intentionally dead until the flag is flipped. */
						if (!SUPPRESS_STATUS_NOTIFICATIONS) {
							toaster.toast({
								message: "Reconnection failed.",
								status: "negative",
							});
						}

						logger.warn(
							"refund: unmatched pos connector closing with error disconnected",
						);

						return void done(
							Page.ErrorDisconnected({
								onClose: closeWithConnectionFailure,
								onRetry,
							}),
							"onClose",
						);
					}

					updateTimeout(async () => {
						// checks api resolution; retries with refreshed token on 401.
						// success permits web socket reconnect, failure signals network
						// error.
						const result = await healthcheck(deviceId);

						if (result instanceof Error) {
							return void done(
								Page.ErrorNetwork({
									onClose: closeWithNetworkFailure,
									onRetry,
								}),
								"onClose",
							);
						}

						_attempt += 1;
						connect();
					}, 1000 * _attempt);
				}

				function onClose(event: CloseEvent) {
					logger.debug("refund: unmatched close", { code: event.code, event });

					if (event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE) {
						return;
					}

					return void reconnect();
				}

				async function onError(event: Event) {
					logger.debug("refund: unmatched error", { event });
					return void reconnect();
				}

				function begin() {
					const payload = RefundUnmatchedRequest();

					updateTimeout(() => {
						// note need to check whether this is possible just yet
						connection.send(CancelRequest());
						return void done(
							Page.ErrorInactivityTimeout({
								onClose: closeWithInactivityTimeout,
								onRetry,
							}),
							"onClose",
						);
					}, STATE_INITIAL_TIMEOUT);

					connection.onMessage(
						handle((message) => {
							{
								// This message is deprecated in favour of
								// `RefundResponseSchema`'s `data.responseCode` of
								// "Q9".
								const event = RefundTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void (() => {
										logger.debug("refund: terminal busy");
										return void done(
											Page.ErrorTerminalBusy({
												onClose: closeWithPairedDeviceBusy,
												onRetry,
											}),
											"onClose",
										);
									})();
								}
							}
							{
								const event = unwrap(
									safe(() => RefundAcknowledgedSchema.parse(message)),
								);
								if (event) {
									setPage(
										Page.AwaitPaymentMethod({
											onClose: cancel,
										}),
									);
									return void listen();
								}
							}
						}),
					);

					connection.send(payload);
					_hasRequested = true;
				}

				function listen() {
					inactivity();
					function inactivity() {
						updateTimeout(() => {
							connection.send(CancelRequest());
							return void done(
								Page.ErrorInactivityTimeout({
									onClose: closeWithInactivityTimeout,
									onRetry,
								}),
								"onClose",
							);
						}, STATE_TRANSACTION_STATE_TIMEOUT);
					}

					function next(page: Page) {
						setPage(page);
						// Reset reconnection attempts when we successfully receive data.
						_attempt = 0;
						// Reset inactivity timeout on every navigation.
						inactivity();
					}

					connection.onMessage(
						handle((message) => {
							{
								// This message is deprecated in favour of
								// `PurchaseResponseSchema`'s `data.status` of "TERMINAL_BUSY".
								// This branch and `PurchaseTerminalBusySchema` can be removed
								// once backend notifies us of its upstream removal.
								const event = RefundTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void (() =>
										done(
											Page.ErrorTerminalBusy({
												onClose: closeWithPairedDeviceBusy,
												onRetry,
											}),
											"onClose",
										))();
								}
							}
							{
								const event = RefundResponseSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										connection.send({
											action: "refund",
											eventId: uuidv4(),
											timestamp: Date.now().toString(),
											data: {
												sessionUuid: sessionId,
												state: "ACKNOWLEDGED",
											},
										});

										if (event.data.status === "APPROVED") {
											return void approved(event);
										} else {
											return void declined(event);
										}
									})();
								}
							}
							{
								const event =
									RefundTransactionEventSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										resolve.Event(event.data.event, {
											message: event.data.displayMessage,
										});

										switch (event.data.event) {
											case "AWAITING_ACCOUNT_SELECTION":
												return next(
													Page.AwaitAccountSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_APPLICATION_SELECTION":
												return next(
													Page.AwaitApplicationSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_PIN_ENTRY":
												return next(
													Page.AwaitPinEntry({
														onClose: cancel,
													}),
												);
											case "PROCESSING":
												return next(Page.AwaitProcessing());
											case "DISPLAYING_PAYMENT_MESSAGE":
											case "RETRYING_PAYMENT_METHOD":
												return next(
													Page.DisplayPaymentMessage({
														onClose: cancel,
													}),
												);
											default:
												logger.debug(
													"refund: unmatched transaction event unhandled variant",
												);
												return never(event.data.event);
										}
									})();
								}
							}
							{
								const event =
									RefundSignatureVerificationSchema.safeParse(message)?.data;
								if (event) {
									return void (() => {
										resolve.Event(event.data.state, {
											message: event.data.signature,
										});

										switch (event.data.state) {
											case "AWAITING_SIGNATURE":
											case "SIGNATURE_CLEARED":
												return next(
													Page.AwaitSignature({
														onClose: cancel,
													}),
												);
											case "SIGNATURE_SUBMITTED": {
												const sendSignatureVerificationEvent = (
													isValid: boolean,
												) => {
													connection.send({
														action: "signatureVerification",
														eventId: uuidv4(),
														timestamp: Date.now().toString(),
														data: {
															event: "SIGNATURE_VERIFICATION",
															isValid,
															sessionUuid: sessionId,
														},
													});
												};
												return next(
													Page.SignatureSubmitted({
														signature: event.data.signature,
														onClose: closeWithCancelled,
														onApprove: () =>
															sendSignatureVerificationEvent(true),
														onDecline: () =>
															sendSignatureVerificationEvent(false),
													}),
												);
											}
											default:
												logger.debug(
													"refund: unmatched signature verification unhandled variant",
												);
												return never(event.data.state);
										}
									})();
								}
							}
						}),
					);
				}

				function approved(event: RefundResponse) {
					const { response, getPageProps } = getApprovedResponse({
						event: event.data,
						reference: request.query.reference ?? "",
					});
					return void done(
						Page.Approved({
							...getPageProps(),
							onDone: () => void resolve(response),
						}),
						"onDone",
					);
				}

				function declined(event: RefundResponse) {
					const response = DeclineResponse_fromRefundPosConnectorResponse({
						data: event.data,
						reference: request.query.reference,
					});
					return void done(
						Page.Declined({
							...getPageProps({ data: event.data }),
							onCancel: () => void resolve(response),
							onRetry,
						}),
						"onCancel",
					);
				}

				function cancel() {
					setPage((prev) => {
						return { ...prev, cancelling: true };
					});

					connection.onMessage(
						handle((message) => {
							const event = RefundResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (event.data.status === "APPROVED") {
										return approved(event);
									} else {
										const response =
											DeclineResponse_fromRefundPosConnectorResponse({
												data: event.data,
												reference: request.query.reference,
											});
										return void done(
											Page.Cancelled({
												onClose: () => void resolve(response),
											}),
											"onClose",
											{ skip: true },
										);
									}
								})();
							}
						}),
					);

					connection.send(CancelRequest());
					updateTimeout(() => {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled }),
							"onClose",
						);
					}, STATE_TRANSACTION_STATE_TIMEOUT);
				}

				function handle(
					callback: (
						message: Identity<Omit<POSConnectorMessage, "error">>,
					) => void,
				) {
					return (message: POSConnectorMessage | Error): void => {
						if (message instanceof Error) {
							return;
						}
						if (message.error) {
							if (message.error.type === "PAIRED_DEVICE_UNREACHABLE") {
								return void done(
									Page.ErrorTerminalUnreachable({
										onClose: closeWithConnectionFailure,
										onRetry,
									}),
									"onClose",
								);
							}
							if (message.error.type === "FORBIDDEN") {
								return void done(
									Page.ErrorTerminalMissing({
										onClose: closeWithConnectionFailure,
									}),
									"onClose",
								);
							}
							return void done(
								Page.ErrorOperation({
									onClose: closeWithConnectionFailure,
									onRetry,
								}),
								"onClose",
							);
						}

						return void callback(message);
					};
				}
			}
		}

		return () => void dispose();
	}, [
		CancelRequest,
		RefundUnmatchedRequest,
		apiClient,
		healthcheck,
		request,
		sessionId,
		setPage,
		toaster,
	]);

	return render(page);
}

const STATE_INITIAL_TIMEOUT = 15000; // 15 seconds
const STATE_TRANSACTION_STATE_TIMEOUT = 40000; // 40 seconds

function closeWithCancelled() {
	return void resolve.Error("Cancelled");
}

function closeWithConnectionFailure() {
	return void resolve.Error("Connection Failure");
}

function closeWithInactivityTimeout() {
	return void resolve.Error("Inactivity Timeout");
}

function closeWithNetworkFailure() {
	return void resolve.Error("Network Failure");
}

function closeWithPairedDeviceBusy() {
	return void resolve.Error("Paired Device Busy");
}

/**
 * I don't want to remove the status notifications because I believe
 * that they are critical to a user's experience of being able to self-
 * diagnose and resolve network failures in high-traffic, customer-
 * facing environments. It is not "cleaner" to hide them; it is
 * obstructing the user's ability to resolve a common system fault in a
 * real-world environment with imperfect network conditions.
 *
 * Set to `false` to restore the reconnecting status notifications.
 * Remove flag when the decision to restore them is confirmed.
 */
const SUPPRESS_STATUS_NOTIFICATIONS = true;
